import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import type { CapacityBinding } from '@bliks-io/contracts';

/** The i18n language is the only locale signal the app carries. */
export function resolveCapacityLocale(language: string): string {
  return language === 'de' ? 'de-DE' : 'en-US';
}

export function formatCapacityHours(hours: number, locale: string): string {
  return hours.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function formatPercent(share: number | null | undefined, locale: string): string | null {
  return share == null
    ? null
    : share.toLocaleString(locale, { style: 'percent', maximumFractionDigits: 0 });
}

export interface CapacityCellProps {
  capacity: CapacityBinding | null | undefined;
  className?: string;
  /**
   * Response coverage as a 0..1 share (ADR 0023 decision 4), rendered as a
   * percent beside the hours. OPT-IN: this component also serves the process
   * list, the systems table and the impacted-processes card, whose bindings are
   * entity aggregates that carry no coverage — passing nothing leaves those
   * surfaces exactly as they were.
   *
   * Rendered even without a capacity: a step where everyone declined has no
   * hours to show and is precisely the one whose coverage explains why.
   */
  confidence?: number | null;
}

/**
 * CapacityCell - A table cell component for displaying capacity binding
 *
 * Features:
 * - Click to toggle between hours per month and hours per year
 * - Tooltip showing cost calculations
 * - Handles null/undefined gracefully (shows "-")
 */
function CapacityCell({ capacity, className, confidence }: CapacityCellProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [showYearly, setShowYearly] = React.useState(false);

  const hasData = capacity !== null && capacity !== undefined;

  const locale = resolveCapacityLocale(i18n.language);

  // The provenance trace (PRD 8.3), tooltip-only like the confidence: a
  // per-step binding names its winning source, an aggregate only says that a
  // manual value is somewhere in the sum.
  const isManual = hasData && capacity.capacitySource === 'manual';
  const carriesManualValues = hasData && capacity.includesManualValues === true;

  const coverage = formatPercent(confidence, locale);

  const displayValue = React.useMemo(() => {
    if (!hasData) return '-';
    const hours = showYearly ? capacity.hoursPerYear : capacity.hoursPerMonth;
    if (hours == null) return '-';
    return `${formatCapacityHours(hours, locale)} h`;
  }, [hasData, capacity, showYearly, locale]);

  const periodSuffix = showYearly
    ? t('organization.systems.capacityCell.yearSuffix')
    : t('organization.systems.capacityCell.monthSuffix');

  const togglePeriod = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowYearly((prev) => !prev);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  if (!hasData) {
    if (coverage === null) {
      return <span className="text-muted-foreground">-</span>;
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="text-muted-foreground focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring rounded-sm px-1 -mx-1"
            >
              -
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="text-xs">{t('processes.responseCoverage', { percent: coverage })}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={togglePeriod}
            className={cn(
              'inline-flex items-center gap-1 text-sm tabular-nums',
              'hover:text-primary cursor-pointer transition-colors',
              'focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring rounded-sm px-1 -mx-1',
              className
            )}
          >
            <span className="font-medium">{displayValue}</span>
            <span className="text-muted-foreground text-xs">{periodSuffix}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2 text-sm">
            <div className="font-medium">{t('organization.systems.stats.costCalculation')}</div>
            <div className="space-y-1">
              <div className="flex justify-between gap-4">
                <span>{t('organization.systems.stats.costPerMonth')}:</span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(capacity.costPerMonth ?? 0)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span>{t('organization.systems.stats.costPerYear')}:</span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(capacity.costPerYear ?? 0)}
                </span>
              </div>
            </div>
            <div className="pt-1 border-t text-xs text-muted-foreground">
              {t('organization.systems.stats.billingRateInfo', {
                rate: formatCurrency(capacity.billingRate ?? 0)
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('organization.systems.stats.stepsWithData', {
                count: capacity.stepsWithData,
                total: capacity.totalSteps
              })}
            </div>
            {coverage !== null && (
              <div className="text-xs text-muted-foreground">
                {/* "Rücklaufquote" is only true of a pure survey binding —
                      a manual value's 90 % and a mixed aggregate's weighted mean
                      are neither of them a response share. */}
                {isManual || carriesManualValues
                  ? t('processes.capacityConfidence', { percent: coverage })
                  : t('processes.responseCoverage', { percent: coverage })}
              </div>
            )}
            {isManual && (
              <div className="text-xs text-muted-foreground">
                {t('processes.capacityManualMark')}
              </div>
            )}
            {carriesManualValues && !isManual && (
              <div className="text-xs text-muted-foreground">
                {t('processes.capacityIncludesManual')}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { CapacityCell };
